import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { ChecklistToggleRow } from '@models/checklist-toggle/checklist-toggle.model';

import { EventType, GqlService } from '@shared/services/gql.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { EventQuery } from '@shared/store/event/event.query';
import { QuarterCloseChecklistRowTitles } from '@shared/store/workflow/workflow-types';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Workflow, WorkflowStore } from '@shared/store/workflow/workflow.store';

import { ChecklistSectionDiscountComponent } from '../components/checklist-section-discount/checklist-section-discount.component';
import { ChecklistSectionGatherContractsComponent } from '../components/checklist-section-gather-contracts/checklist-section-gather-contracts.component';
import { ChecklistSectionGatherEstimatesComponent } from '../components/checklist-section-gather-estimates/checklist-section-gather-estimates.component';
import { ChecklistSectionGatherPatientsComponent } from '../components/checklist-section-gather-patients/checklist-section-gather-patients.component';
import {
  ChecklistComponent,
  QuarterCloseChecklistRowComponents,
  QuarterCloseChecklistSection,
  QuarterCloseChecklistSectionTitles,
  QuarterCloseChecklistVendorEstimateSummary,
} from '../models/quarter-close-checklist.model';
import { filterVendorEstimates } from '../utils/filter-vendor-estimates';

@Injectable({
  providedIn: 'root',
})
export class QuarterCloseChecklistService {
  private eventQuery = inject(EventQuery);
  private gqlService = inject(GqlService);
  private originalWorkflowService = inject(WorkflowService);
  private periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private workflowQuery = inject(WorkflowQuery);
  private workflowStore = inject(WorkflowStore);

  // cache invalidate refreshes checklist and so discount component is gonna destroyed
  // this is just to store the latest selected vendor. This way discount component can
  // re-select the vendor after refresh.
  discountSelectedVendor = '';
  RowComponents: QuarterCloseChecklistRowComponents = {
    CloseDiscounts: new ChecklistComponent(ChecklistSectionDiscountComponent, {}, 'CloseDiscounts'),
    GatherContracts: new ChecklistComponent(
      ChecklistSectionGatherContractsComponent,
      {},
      'GatherContracts'
    ),
    GatherEstimates: new ChecklistComponent(
      ChecklistSectionGatherEstimatesComponent,
      {},
      'GatherEstimates'
    ),
    GatherPatients: new ChecklistComponent(
      ChecklistSectionGatherPatientsComponent,
      {},
      'GatherPatients'
    ),
  };
  Rows = ChecklistToggleRow;
  RowTitles = QuarterCloseChecklistRowTitles;
  Sections = QuarterCloseChecklistSection;
  SectionTitles = QuarterCloseChecklistSectionTitles;

  isAdminUser(): Observable<boolean> {
    return this.periodCloseService.isAdminUser;
  }

  isClosedMonthsProcessing(): Observable<boolean | null> {
    return this.eventQuery.selectProcessingEvent$(EventType.CLOSE_TRIAL_MONTH);
  }

  isCurrentQuarterSelected(): Observable<boolean> {
    return this.periodCloseService.isCurrentQuarterSelected;
  }

  isQuarterCloseEnabled(): Observable<boolean> {
    return this.workflowQuery.isQuarterCloseEnabled$;
  }

  onQuarterMonthChange(selectedQuarterMonth: string): void {
    if (selectedQuarterMonth) {
      const workflowDate = dayjs(selectedQuarterMonth).format('MMM-YYYY').toUpperCase();

      this.originalWorkflowService.getWorkflowList(workflowDate).pipe(first()).subscribe();
    }
  }

  resetWorkflowState() {
    this.workflowStore.remove(() => true);
    this.workflowStore.update({ loading: false });
  }

  vendorEstimateSummaries(
    period: string
  ): Observable<QuarterCloseChecklistVendorEstimateSummary[]> {
    return this.gqlService
      .listVendorEstimateSummaries$(period)
      .pipe(map(filterVendorEstimates.bind(this)));
  }

  workflowAdjustmentAvailable(): Observable<boolean> {
    return this.workflowQuery.getInMonthAdjustmentAvailability$();
  }

  workflowList(): Observable<Workflow[]> {
    return this.workflowQuery.selectAll();
  }

  workflowLoading(): Observable<boolean> {
    return this.workflowQuery.selectLoading();
  }
}
